import { useEffect, useState } from 'react';
import { api, can, type MeView } from './api.ts';
import { returnedFromStripe } from './accountSuspension.ts';
import { Billing } from './Billing.tsx';
import './billing.css';
import './shell.css';

/**
 * What a billing-suspended account sees instead of the console (ADR-0083,
 * amendment).
 *
 * No navigation, no clients and no data: the server refuses all of it with
 * 403, and a shell full of screens that cannot load reads as the product being
 * broken rather than as an invoice to pay. Owners get the Billing screen they
 * already know, because its Subscribe and Manage billing buttons are exactly
 * the way back. Everybody else can only be told whom to ask.
 */

/** How often, and how many times, to ask whether Stripe's webhook has arrived. */
const POLL_MS = 5000;
const POLL_TIMES = 12;

interface RestoreAccessProps {
  readonly me: MeView;
  readonly busy: boolean;
  readonly onSignOut: () => void;
  /** Called with the fresh identity once the account is usable again. */
  readonly onRestored: (me: MeView) => void;
}

export const RestoreAccess = ({ me, busy, onSignOut, onRestored }: RestoreAccessProps) => {
  const [waiting, setWaiting] = useState(() => returnedFromStripe(window.location.search));
  const [checking, setChecking] = useState(false);

  const check = async (): Promise<boolean> => {
    const next = await api.me();
    if (next.restricted !== null) return false;
    onRestored(next);
    return true;
  };

  /*
   * Back from Stripe: the payment has usually gone through, but the webhook
   * that lifts the suspension can land a few seconds after the browser does.
   * Asked again every few seconds for a minute rather than once, so the owner
   * who has just paid watches the console come back instead of being told it
   * is still suspended.
   */
  useEffect(() => {
    if (!waiting) return;
    let left = POLL_TIMES;
    const timer = window.setInterval(() => {
      left -= 1;
      void check()
        .catch(() => false)
        .then((restored) => {
          if (restored || left <= 0) {
            window.clearInterval(timer);
            setWaiting(false);
          }
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
    // Keyed on `waiting` alone: `check` closes over props that do not change
    // while this screen is up, and re-arming the timer on every render would
    // restart the minute each time.
  }, [waiting]);

  const checkNow = async () => {
    setChecking(true);
    try {
      await check();
    } catch {
      // The screen stays as it is; the next click or poll asks again.
    } finally {
      setChecking(false);
    }
  };

  const owner = can(me, 'billing.manage');

  return (
    <div className="signin restore">
      <main id="content" className="restore-main">
        <h1>Restore access</h1>
        {owner ? (
          <p className="lead">
            This account is suspended because its trial or subscription has ended. Nothing already
            collected has been touched. Paying below restores the whole console, usually within a
            minute.
          </p>
        ) : (
          <div className="callout callout-caution">
            <span className="callout-title">This account is suspended</span>
            Its trial or subscription has ended, and only an owner of your organisation can pay to
            restore it. Ask one of them to sign in and open Billing; nothing already collected has
            been touched.
          </div>
        )}

        {waiting && (
          <p className="note" role="status">
            Waiting for Stripe to confirm the payment…
          </p>
        )}

        {owner && <Billing me={me} restoring />}

        <div className="toolbar">
          <button
            type="button"
            className="btn"
            aria-busy={checking || undefined}
            disabled={checking}
            onClick={() => void checkNow()}
          >
            {checking ? 'Checking…' : 'Check again'}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            aria-busy={busy || undefined}
            disabled={busy}
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
};
